import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { shiftSchema } from "../../config/forms/shiftSchema";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DATE_FORMATS } from "../../utils/Constants";
import { useTranslation } from "react-i18next";
const PALETTE = [
  "#FF5722",
  "#FFC107",
  "#8BC34A",
  "#03A9F4",
  "#E91E63",
  "#9C27B0",
  "#673AB7",
  "#3F51B5",
  "#009688",
  "#795548",
];
const styles = {
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: 400 },
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: { xs: 2.5, sm: 4 },
    outline: "none",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  title: {
    color: "#F7A86E",
    fontWeight: 700,
    mb: 3,
    textAlign: "center",
  },
  inputError: {
    ml: 1,
  },
  colorLabel: {
    mb: 1,
    color: "#666",
  },
  colorPaletteContainer: {
    display: "flex",
    gap: 1,
    flexWrap: "wrap",
  },
  colorCircle: (c, isUsed, isSelected) => ({
    width: 32,
    height: 32,
    borderRadius: "50%",
    bgcolor: c,
    cursor: isUsed ? "not-allowed" : "pointer",
    opacity: isUsed ? 0.3 : 1,
    border: isSelected ? "3px solid #333" : "2px solid transparent",
    transition: "all 0.2s",
    "&:hover": {
      transform: !isUsed ? "scale(1.1)" : "none",
    },
  }),
  colorError: {
    mt: 1,
    display: "block",
  },
  buttonContainer: {
    display: "flex",
    gap: 2,
    mt: 2,
  },
  cancelButton: {
    borderColor: "#F7A86E",
    color: "#F7A86E",
    fontWeight: 600,
    "&:hover": {
      borderColor: "#E69155",
      bgcolor: "rgba(247, 168, 110, 0.04)",
    },
  },
  confirmButton: {
    background: "linear-gradient(90deg, #E2A532 0%, #F79072 100%)",
    color: "#fff",
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(247, 144, 114, 0.3)",
    },
  },
};
const CreateShiftModal = ({
  open,
  onClose,
  onCreate,
  usedColors = [],
  existingShiftTypes = [],
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(shiftSchema),
    defaultValues: {
      start_time: null,
      end_time: null,
      break_hours: "",
      shift_type: "",
      color: "",
    },
  });
  const { t } = useTranslation();
  const selectedColor = watch("color");
  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      start_time: dayjs(data.start_time).format("HH:mm:ss"),
      end_time: dayjs(data.end_time).format("HH:mm:ss"),
      break_hours: Number(data.break_hours),
    };
    onCreate(formattedData);
    reset({
      start_time: null,
      end_time: null,
      break_hours: "",
      shift_type: "",
      color: "",
    });
  };
  const handleClose = () => {
    reset();
    onClose();
  };
  return (
    <Modal open={open} onClose={handleClose} keepMounted={false}>
      <Box sx={styles.modalBox}>
        <Typography variant="h6" sx={styles.title}>
          {t("pages.shift_management.create_shift_title")}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* Start Time */}
              <Grid item xs={6}>
                <Controller
                  name="start_time"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label={t("pages.shift_management.start_label")}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.start_time,
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                />
                {errors.start_time && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={styles.inputError}
                  >
                    {errors.start_time.message}
                  </Typography>
                )}
              </Grid>
              {/* End Time */}
              <Grid item xs={6}>
                <Controller
                  name="end_time"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label={t("pages.shift_management.end_label")}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.end_time,
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                />
                {errors.end_time && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={styles.inputError}
                  >
                    {errors.end_time.message}
                  </Typography>
                )}
              </Grid>
              {/* Break Hours */}
              <Grid item xs={12}>
                <Controller
                  name="break_hours"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("pages.shift_management.break_time_label")}
                      fullWidth
                      size="small"
                      type="number"
                      error={!!errors.break_hours}
                      helperText={errors.break_hours?.message}
                    />
                  )}
                />
              </Grid>
              {/* Shift Type */}
              <Grid item xs={12}>
                <Controller
                  name="shift_type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("pages.shift_management.shift_type_placeholder")}
                      fullWidth
                      size="small"
                      error={!!errors.shift_type}
                      helperText={errors.shift_type?.message}
                    />
                  )}
                />
              </Grid>
              {/* Color Picker */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={styles.colorLabel}>
                  {t("pages.shift_management.shift_color_label")}
                </Typography>
                <Box sx={styles.colorPaletteContainer}>
                  {PALETTE.map((c) => {
                    const isUsed = usedColors.includes(c);
                    const isSelected = selectedColor === c;
                    return (
                      <Box
                        key={c}
                        onClick={() => !isUsed && setValue("color", c)}
                        sx={styles.colorCircle(c, isUsed, isSelected)}
                      />
                    );
                  })}
                </Box>
                {errors.color && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={styles.colorError}
                  >
                    {errors.color.message}
                  </Typography>
                )}
              </Grid>
              {/* Buttons */}
              <Grid item xs={12} sx={styles.buttonContainer}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClose}
                  sx={styles.cancelButton}
                >
                  {t("pages.shift_management.cancel")}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  sx={styles.confirmButton}
                >
                  {t("pages.shift_management.confirm")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </LocalizationProvider>
      </Box>
    </Modal>
  );
};

export default CreateShiftModal;
